// ethService.js
import { getBalance } from '@wagmi/core';
import { config } from './walletConfig';
import utils from '@/composables/utils'; // 包含 convertFromWei
import axios from 'axios'; // 引入axios用于HTTP请求

const { convertFromWei } = utils;

// One queue per page prevents simultaneous history panels exhausting the 3/sec API quota.
let historyQueue = Promise.resolve();
let lastHistoryRequest = 0;
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));
async function requestHistory(url, params) {
  const task = historyQueue.then(async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const delay = 400 - (Date.now() - lastHistoryRequest);
      if (delay > 0) await wait(delay);
      lastHistoryRequest = Date.now();
      try {
        const response = await axios.get(url, { params, timeout: 10000 });
        const limited = response.data?.status === '0' && /rate limit|max calls/i.test(String(response.data.result));
        if (!limited || attempt === 2) return response;
      } catch (error) {
        if (error.response?.status !== 429 || attempt === 2) throw error;
      }
      await wait(1000 * (attempt + 1));
    }
  });
  historyQueue = task.catch(() => {});
  return task;
}

/**
 * 查询主链币余额（如ETH、MATIC、BNB等）
 * @param {string} account 钱包地址
 * @param {number} decimals 精度（ETH/MATIC/BNB等为18）
 * @param {number} fixed 保留小数位数，默认4位
 * @returns {Promise<string>} 格式化后的余额
 */
export async function getEthBalance(account, decimals = 18, fixed = 4) {
  try {
    const balance = await getBalance(config, { address: account });
    return convertFromWei(balance.value, decimals, fixed);
  } catch (error) {
    console.error('获取主链币余额失败:', error);
    throw error;
  }
}

/**
 * 查询MATIC（原POL）的实时价格
 * @returns {Promise<number>} MATIC的实时价格
 */
export async function getMaticPrice() {
  try {
    const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=matic-network&vs_currencies=usd'); // 使用CoinGecko的API获取MATIC价格
    console.log('matic-network',response.data['matic-network'].usd)
    return response.data['matic-network'].usd; // 假设API返回的价格在data['matic-network'].usd中
  } catch (error) {
    console.error('获取MATIC价格失败:', error);
    throw error;
  }
}

/**
 * 通過 Etherscan/Polygonscan V2 API 查詢合約事件日誌（支持多 topic 過濾、分頁、指定區塊範圍等）
 * 官方文檔：https://docs.etherscan.io/api-endpoints/logs#get-logs
 * @param {Object} params 查詢參數
 * @param {string} params.apiKey Etherscan/Polygonscan API KEY
 * @param {string} params.contractAddress 合約地址
 * @param {string} params.topic0 事件 topic0（事件簽名 hash）
 * @param {string} [params.topic1] topic1（如 user 地址，需補 0 到 64 位）
 * @param {number|string} [params.fromBlock=0] 起始區塊
 * @param {number|string} [params.toBlock='latest'] 結束區塊
 * @param {string} [params.apiBase='https://api.etherscan.io/v2'] API 基礎 URL（V2接口）
 * @param {number|string} [params.chainId=137] 鏈 ID，預設 137（POLYGON 主網）
 * @param {string} [params.address] 查詢特定地址（如 user 地址，會自動補全 topic1）
 * @param {number} [params.page=1] 分頁頁碼
 * @param {number} [params.offset=1000] 每頁返回數量
 * @param {string} [params.topic0_1_opr] topic0 和 topic1 的邏輯運算（如 'and'）
 * @returns {Promise<Array>} 事件 log 陣列
 *
 * 用法範例：
 *   const logs = await queryContractEventsByApi({
 *     apiKey: 'YourApiKeyToken',
 *     contractAddress: '0x59728544b08ab483533076417fbbb2fd0b17ce3a',
 *     topic0: '0x27c4f0403323142b599832f26acd21c74a9e5b809f2215726e244a4ac588cd7d',
 *     topic1: '0x00000000000000000000000023581767a106ae21c074b2276d25e5c3e136a68b',
 *     fromBlock: 15073139,
 *     toBlock: 15074139,
 *     chainId: 1,
 *     page: 1,
 *     offset: 1000,
 *     topic0_1_opr: 'and'
 *   });
 */
export async function queryContractEventsByApi({
  apiKey = '4JYZBNZFNMQFBWUJ247ICYKQB1IUWGGF4Y',
  contractAddress,
  topic0,
  topic1 = undefined,
  fromBlock = 0,
  toBlock = 'latest',
  apiBase = 'https://api.etherscan.io/v2',
  chainId = 137,
  address = undefined,
  page = 1,
  offset = 1000,
  // 是的，這個參數 topic0_1_opr 用於指定 topic0 和 topic1 的邏輯運算，常見值為 'and'
  topic0_1_opr = 'and',
}) {
  try {
    // 如果有 address 參數，則自動補全 topic1（補 0 到 64 位）
    let _topic1 = topic1;
    if (address) {
      const addr = address.toLowerCase().replace(/^0x/, '');
      _topic1 = '0x' + addr.padStart(64, '0');
    }

    // 組裝查詢參數
    if (chainId !== 137) throw new Error('只支援 Polygon 記錄查詢');
    if (address && !/^0x[0-9a-fA-F]{40}$/.test(address)) throw new Error('無效的錢包地址');
    const params = {
      chainid: chainId,
      module: 'logs',
      action: 'getLogs',
      fromBlock,
      toBlock,
      address: contractAddress,
      topic0,
      apikey: apiKey,
      page,
      offset,
      ...(_topic1 ? { topic1: _topic1, topic0_1_opr } : {}),
    };

    // if (_topic1) params.topic1 = _topic1;
    // if (topic0_1_opr) params.topic0_1_opr = topic0_1_opr;

    const url = `${apiBase}/api`;
    const response = await requestHistory(url, params);
    // console.log('response', response);
    // 處理返回格式
    if (response.data.status === '1') {
      // 查詢成功，返回事件 log 陣列
      if (!Array.isArray(response.data.result)) throw new Error('事件資料格式錯誤');
      return response.data.result;
    } else if (response.data.status === '0' && response.data.message === 'No records found') {
      // 沒有查到記錄，返回空陣列
      return [];
    } else {
      // 其他錯誤
      throw new Error(response.data.result || 'API查詢失敗');
    }
  } catch (error) {
    console.error('[queryContractEventsByApi] 查詢失敗:', error);
    throw error;
  }
}

// Etherscan getLogs is ascending: fetch all bounded pages before choosing newest.
// Never silently return an incomplete first page as the user's latest history.
export async function queryRecentContractEvents(params, count = 5) {
  const records = [];
  for (let page = 1; page <= 10; page++) {
    const logs = await queryContractEventsByApi({ ...params, page, offset: 1000 });
    records.push(...logs);
    if (logs.length < 1000) {
      const topic1 = params.address ? '0x' + params.address.slice(2).toLowerCase().padStart(64, '0') : null;
      const unique = new Map();
      for (const log of records) {
        if (topic1 && log.topics?.[1]?.toLowerCase() !== topic1) continue;
        unique.set(`${log.transactionHash}:${log.logIndex}`, log);
      }
      return [...unique.values()].sort((a, b) => Number(b.blockNumber) - Number(a.blockNumber)
        || Number(b.logIndex) - Number(a.logIndex)).slice(0, count);
    }
  }
  throw new Error('歷史記錄超過查詢上限，請稍後重試或在區塊瀏覽器查看。');
}
